/* eslint-disable max-len */
// noinspection JSUnusedGlobalSymbols
/**
 * @author tht7 ( tht7 )
 * @date 10/07/12022 6:36 pm
 */

import { CandidateSessionWarnings, ICandidateSession } from '../interfaces/candidateSessionInterface';

const isDevelopmentEnv = process.env.NODE_ENV === 'development';

let privateBaseUrl = '';

/** *
 * this will listen for when the page is closed and send one last update!
 */
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden' && window.candidateSession) {
    navigator.sendBeacon(`${privateBaseUrl}/applications/session`, JSON.stringify({
      ...window.candidateSession,
      page_closed: new Date(),
    }));
  }
});

// /**
//  * This function calls the backend to update the session
//  * but with an empty update is just "fetch"s the session object from the database
//  * this is a supporting function for the getCandidateSession and should not be used directly
//  * @param session_id
//  */
// async function getSessionFromServer(session_id?: string):
// Promise<ICandidateSession | undefined> {
//   try {
//     return (await axiosInstance.post('/applications/session', { session_id })
//       .then((res) => res.data.data as ICandidateSession));
//   } catch (ignore) {
//     console.error('failed to fetch candidate session from server', ignore);
//   }
//   return undefined;
// }

/**
 * This function will update the session in the database and cache it in the window object
 * it will fill in the current session over the provided object
 * @param session
 * @param baseURL
 */
export async function updateSession(session: Partial<ICandidateSession>, baseURL = '')
  : Promise<ICandidateSession | undefined> {
  try {
    if (isDevelopmentEnv) {
      return await Promise.resolve(undefined);
    }
    privateBaseUrl = baseURL || privateBaseUrl;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const session_id = sessionStorage.getItem(window.location.href);
    // calling getCandidateSession will
    // update the session in the session storage and window.candidateSession
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return await getCandidateSession(
      await fetch(`${privateBaseUrl}/applications/session`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(session_id && { session_id }),
          ...window.candidateSession,
          ...session,
        }),
      })
        .then((res) => res.json())
        .then((res) => res as ICandidateSession),
    );
  } catch (ignore) {
    console.error('failed to update candidate session', ignore);
  }
  return undefined;
}

/**
 * This function will fetch the session from the sessionStorage and server
 * it will then cache the session in the window object
 * @param _session - in case you want to override the session, or save a new one to the cache
 * @param _session_id - in case you want to override the session_id and fetch from the server
 */
export async function getCandidateSession(_session?: ICandidateSession, _session_id?: string)
  : Promise<ICandidateSession | undefined> {
  try {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const session_id = _session_id || _session?.session_id || sessionStorage.getItem(window.location.href) || '';
    window.candidateSession = _session || window.candidateSession
        || await updateSession({ session_id });

    if (!window.candidateSession?.session_id) {
      window.candidateSession = await updateSession({ session_id });
    }
    if (window.candidateSession) {
      sessionStorage[window.location.href] = window.candidateSession.session_id;
    }
  } catch (ignore) {
    console.error('failed to fetch candidate session', ignore);
  }
  return window.candidateSession;
}

export function getSessionIdFromCache(): string | undefined | null {
  return window?.candidateSession?.session_id || sessionStorage.getItem(window.location.href);
}

/**
 * this will remove the session from the sessionStorage, but not the window cache
 * this should signal the end of this session
 * however the end of a session is when the window gets closed
 * so in order to prevent a new window from (maybe) getting the stale session
 * we remove it from the sessionStorage
 */
export function deleteSession(): void {
  sessionStorage.removeItem(window.location.href);
}

/** ehehehe this is a type that takes 2 types and matches all the keys of type V from type K */
type KeysMatching<T, V> = { [K in keyof T]-?: T[K] extends (V | undefined) ? K : never } [keyof T];

/**
 * A cute shortcut for updating a date event in the session
 * just give it the key of the date event, and it will update it
 * @param eventType
   */
export async function registerDateEvent(eventType: KeysMatching<ICandidateSession, Date>)
  : Promise<ICandidateSession | undefined> {
  const session = await getCandidateSession();
  if (!session) return undefined;

  session[eventType] = new Date();
  if (!session.first_interaction) {
    session.first_interaction = session[eventType];
  }
  if (session) {
    return updateSession(session);
  }
  return undefined;
}

/**
 * Another cute shortcut for updating a number state of a session
 * if no data is provided, it will just increment the number
 * @param eventType
 * @param data
 */
export async function registerCountEvent(
  eventType: KeysMatching<ICandidateSession, number>,
  data?: number,
)
  : Promise<ICandidateSession | undefined> {
  const session = await getCandidateSession();
  if (!session) return undefined;

  session[eventType] = data || (session[eventType] || 0) + 1;
  if (session) {
    return updateSession(session);
  }
  return undefined;
}
/**
 * Another cute shortcut for updating a number state of a session
 * if no data is provided, it will just increment the number
 * @param warning
 */
export async function registerWarning(
  warning: string,
)
  : Promise<ICandidateSession | undefined> {
  const session = await getCandidateSession();
  if (!session) return undefined;
  session.warnings ||= [];
  session.warnings.concat(warning as (keyof typeof CandidateSessionWarnings));
  if (session) {
    return updateSession(session);
  }
  return undefined;
}
export const triggerEvent = {
  warning: registerWarning,
  date: registerDateEvent,
  number: registerCountEvent,
};
